import { useEffect, useState } from 'react';
import { explorerTxUrl, formatUsd, type Chain } from '@ithildin/shared';

import type { EscrowView } from '../lib/api';

/** Under six hours, a reply window is worth flagging as urgent. */
const URGENT_MS = 6 * 3_600_000;

export interface MeterState {
  /** Fraction of the reply window still open, 0–1. */
  remaining: number;
  urgent: boolean;
  expired: boolean;
  label: string;
}

export function meterState(fundedAt: string, deadlineAt: string, now = Date.now()): MeterState {
  const start = new Date(fundedAt).getTime();
  const end = new Date(deadlineAt).getTime();
  const span = Math.max(end - start, 1);
  const left = end - now;

  if (left <= 0) return { remaining: 0, urgent: false, expired: true, label: 'window closed' };

  const hours = Math.floor(left / 3_600_000);
  const mins = Math.floor((left % 3_600_000) / 60_000);
  const label =
    hours >= 24
      ? `${Math.floor(hours / 24)}d ${hours % 24}h left`
      : hours > 0
        ? `${hours}h ${mins}m left`
        : `${mins}m left`;

  return {
    remaining: Math.min(Math.max(left / span, 0), 1),
    urgent: left < URGENT_MS,
    expired: false,
    label,
  };
}

/**
 * Amount and clock as a single object: ledger figures over a hairline that
 * drains as the window closes. Gold while the money is live, verdigris once it
 * is paid, struck through when it went back to the sender.
 */
export function EscrowMeter({ escrow }: { escrow: EscrowView }) {
  const [state, setState] = useState(() => meterState(escrow.fundedAt, escrow.deadlineAt));

  useEffect(() => {
    setState(meterState(escrow.fundedAt, escrow.deadlineAt));
    const id = setInterval(
      () => setState(meterState(escrow.fundedAt, escrow.deadlineAt)),
      30_000,
    );
    return () => clearInterval(id);
  }, [escrow.fundedAt, escrow.deadlineAt]);

  const amount = formatUsd(escrow.amountMicros);

  // Earned but not yet settled on-chain. A countdown here would wrongly suggest
  // the money is still at risk when the reply has already secured it.
  if (escrow.status === 'PENDING' && escrow.repliedAt) {
    return (
      <div className="meter">
        <span className="meter-amount is-settled">{amount}</span>
        <div className="meter-track">
          <div className="meter-fill is-settled" style={{ width: '100%' }} />
        </div>
        <span className="meter-clock is-settled">earned · settling</span>
      </div>
    );
  }

  if (escrow.status === 'RELEASED') {
    return (
      <div className="meter">
        <span className="meter-amount is-settled">{amount}</span>
        <div className="meter-track">
          <div className="meter-fill is-settled" style={{ width: '100%' }} />
        </div>
        <span className="meter-clock is-settled">paid out</span>
      </div>
    );
  }

  if (escrow.status === 'REFUNDED') {
    return (
      <div className="meter">
        <span className="meter-amount is-returned">{amount}</span>
        <div className="meter-track">
          <div className="meter-fill is-spent" style={{ width: '0%' }} />
        </div>
        <span className="meter-clock">returned</span>
      </div>
    );
  }

  const tone = state.urgent || state.expired ? ' is-urgent' : '';
  return (
    <div className="meter">
      <span className="meter-amount">{amount}</span>
      <div className="meter-track">
        <div className={`meter-fill${tone}`} style={{ width: `${state.remaining * 100}%` }} />
      </div>
      <span className={`meter-clock${tone}`}>{state.label}</span>
    </div>
  );
}

/** The same object at display size, for the landing page. */
export function EscrowMeterLarge({
  amountMicros,
  remaining,
  label,
}: {
  amountMicros: string;
  remaining: number;
  label: string;
}) {
  const urgent = remaining < 0.15;
  return (
    <div className="meter" style={{ alignItems: 'flex-start', width: '100%' }}>
      <span className="meter-amount" style={{ fontSize: '2.5rem' }}>
        {formatUsd(amountMicros)}
      </span>
      <div className="meter-track" style={{ height: 3, marginTop: 4 }}>
        <div
          className={`meter-fill${urgent ? ' is-urgent' : ''}`}
          style={{ width: `${remaining * 100}%` }}
        />
      </div>
      <span className={`meter-clock${urgent ? ' is-urgent' : ''}`}>{label}</span>
    </div>
  );
}

/**
 * Link to the funding and settlement transactions. The pitch is that the money
 * is really locked on-chain; a sender should be able to check that themselves
 * rather than take our word for it.
 */
export function EscrowReceipts({ escrow }: { escrow: EscrowView }) {
  const chain = escrow.chain as Chain;
  return (
    <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
      {escrow.fundingTxSignature && (
        <a
          className="receipt"
          href={explorerTxUrl(chain, escrow.fundingTxSignature, 'devnet')}
          target="_blank"
          rel="noreferrer noopener"
        >
          funding tx ↗
        </a>
      )}
      {escrow.settlementTxSignature && (
        <a
          className="receipt"
          href={explorerTxUrl(chain, escrow.settlementTxSignature, 'devnet')}
          target="_blank"
          rel="noreferrer noopener"
        >
          settlement tx ↗
        </a>
      )}
    </div>
  );
}

/** Banner above a paid conversation. States the outcome for both sides. */
export function EscrowBanner({
  escrow,
  viewerIsRecipient,
}: {
  escrow: EscrowView;
  viewerIsRecipient: boolean;
}) {
  const cls =
    escrow.status === 'RELEASED'
      ? ' is-settled'
      : escrow.status === 'REFUNDED'
        ? ' is-returned'
        : '';

  return (
    <div className={`escrow-banner${cls}`} style={{ alignItems: 'flex-start' }}>
      <EscrowMeter escrow={escrow} />
      <div>
        <p>
        {escrow.status === 'RELEASED'
          ? `Released to ${viewerIsRecipient ? 'your wallet' : 'the recipient'} after the reply.`
          : escrow.status === 'REFUNDED'
            ? `The window closed unanswered, so it went back to ${viewerIsRecipient ? 'the sender' : 'your wallet'}.`
            : viewerIsRecipient
              ? 'Reply before the window closes and this is yours. Otherwise it returns to the sender.'
              : 'Held until they reply. If they stay silent, it comes back to you in full.'}
        </p>
        <EscrowReceipts escrow={escrow} />
      </div>
    </div>
  );
}
